/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_CONNECTION_KEY, EConnectionType } from '../const';
import {
  removeStoredConnectionType,
  restoreConnectionType,
  storeConnectionType,
} from '../utils/storeConnectionType';

export interface IConnectionStateItem {
  key: string;
  isConnected: boolean;
  chainId: number | null;
  accountAddress: string | null;
  walletIcon: string | null;
  disconnect: () => Promise<void>;
}

export interface IConnectionState {
  type: EConnectionType | null;
  connections: Record<EConnectionType, IConnectionStateItem>;
}

interface IRootState {
  connection: IConnectionState;
}

export const DEFAULT_CONNECTION_STATE = {
  key: DEFAULT_CONNECTION_KEY,
  isConnected: false,
  chainId: null,
  accountAddress: null,
  walletIcon: null,
  disconnect: () => Promise.resolve(),
};

const initialState: IConnectionState = {
  type: restoreConnectionType() ?? null,
  connections: {
    [EConnectionType.MatchId]: DEFAULT_CONNECTION_STATE,
    [EConnectionType.EthWallet]: DEFAULT_CONNECTION_STATE,
  },
};

export const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    setConnectionType: (
      state,
      action: PayloadAction<EConnectionType | null>,
    ) => {
      if (action.payload === null) {
        removeStoredConnectionType();
        state.type = null;
        return;
      }

      storeConnectionType(action.payload);
      state.type = action.payload;
    },

    setConnectionKey: (
      state,
      action: PayloadAction<{ key: string; type: EConnectionType }>,
    ) => {
      const { type, key } = action.payload;

      state.connections[type].key = key;
    },

    setConnectionState: (
      state,
      action: PayloadAction<
        Partial<IConnectionStateItem> & { type: EConnectionType }
      >,
    ) => {
      const {
        type,
        key,
        isConnected,
        chainId,
        accountAddress,
        disconnect,
        walletIcon,
      } = action.payload;

      const previousConnection = state.connections[type];

      state.connections[type] = {
        ...previousConnection,
        key: key ?? previousConnection.key,
        isConnected: isConnected ?? previousConnection.isConnected,
        chainId: chainId ?? previousConnection.chainId,
        accountAddress: accountAddress ?? previousConnection.accountAddress,
        disconnect: disconnect ?? previousConnection.disconnect,
        walletIcon: walletIcon ?? previousConnection.walletIcon,
      };
    },

    resetConnectionState: (state, action: PayloadAction<EConnectionType>) => {
      removeStoredConnectionType();
      state.connections[action.payload] = DEFAULT_CONNECTION_STATE;
    },
  },
});

export const selectConnection = (state: IRootState) => {
  const { type } = state.connection;

  const isNotExist = !type || !Object.values(EConnectionType).includes(type);
  if (isNotExist) {
    return DEFAULT_CONNECTION_STATE;
  }

  return state.connection.connections[type];
};

export const selectConnectionType = (state: IRootState) =>
  state.connection.type;

export const {
  setConnectionType,
  setConnectionKey,
  setConnectionState,
  resetConnectionState,
} = connectionSlice.actions;
